import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Shield, Anchor, Eye, Gauge, Info } from "lucide-react";
import { toast } from "sonner";
import { GroundingExercise } from "./exercises/GroundingExercise";
import { SafePlaceVisualization } from "./exercises/SafePlaceVisualization";

interface TraumaToolsProps {
  user: any;
}

export const TraumaTools = ({ user }: TraumaToolsProps) => {
  const [activeTab, setActiveTab] = useState('grounding');
  const [activeExercise, setActiveExercise] = useState<string | null>(null);

  const tools = [
    {
      id: 'grounding',
      name: 'Grounding Techniques',
      icon: Anchor,
      description: 'Anchor yourself in the present moment when feeling triggered or dissociated',
      color: 'text-primary',
      bgColor: 'bg-primary/10',
      why: 'Trauma survivors often experience flashbacks, intrusive memories, or dissociation-feeling disconnected from the present. Grounding techniques help you return to the "here and now" by engaging your senses and orienting yourself to your current, safe reality.',
      how: '5-4-3-2-1 method: Name 5 things you can see, 4 you can touch, 3 you can hear, 2 you can smell, 1 you can taste. Other options: Hold ice, describe your surroundings in detail, press your feet into the floor, splash cold water on your face, name the date/time/location.',
      science: 'Grounding works by activating your prefrontal cortex (thinking brain) and redirecting attention away from the amygdala\'s alarm signals. It interrupts the freeze/fight/flight response and helps your nervous system recognise you\'re currently safe.'
    },
    {
      id: 'flashbacks',
      name: 'Flashback Protocol',
      icon: Eye,
      description: 'Step-by-step response plan for when traumatic memories intrude',
      color: 'text-secondary',
      bgColor: 'bg-secondary/10',
      why: 'Flashbacks make the past feel present-your brain genuinely believes the trauma is happening now. A flashback protocol provides a structured way to remind yourself that you\'re safe, orient to the present, and ride out the experience without adding fear on top of fear.',
      how: '1) Tell yourself: "I\'m having a flashback. This is a memory, not happening now." 2) Ground yourself using sensory techniques. 3) Breathe slowly. 4) Look around and describe what you see. 5) Remind yourself of the date and your current safe location. 6) Use a grounding object. 7) Reach out to support if needed.',
      science: 'Flashbacks occur because traumatic memories are stored differently than regular memories-without proper time stamps or context markers. The protocol helps your brain re-categorize the memory as "past" rather than "present threat."'
    },
    {
      id: 'window-tolerance',
      name: 'Window of Tolerance',
      icon: Gauge,
      description: 'Understand and widen your capacity to handle emotions and stress',
      color: 'text-primary',
      bgColor: 'bg-primary/10',
      why: 'Trauma narrows your "window of tolerance"-the zone where you can think and feel without becoming overwhelmed (hyperarousal) or shutting down (hypoarousal). Understanding this helps you recognise your states and take action to return to the window.',
      how: 'Learn to identify: Hyperarousal signs (anxiety, panic, racing heart, agitation, hypervigilance), Hypoarousal signs (numbness, disconnection, exhaustion, fog), and your Window (feeling present, able to respond, grounded). Use activating techniques for hypoarousal, calming techniques for hyperarousal.',
      science: 'The window of tolerance concept comes from Dr. Dan Siegel\'s work on interpersonal neurobiology. Trauma dysregulates the autonomic nervous system, pushing you outside the window more easily. With practice, you can widen this window and return to it more quickly.'
    },
    {
      id: 'safe-place',
      name: 'Safe Place Imagery',
      icon: Shield,
      description: 'Create a mental sanctuary you can access when feeling unsafe',
      color: 'text-secondary',
      bgColor: 'bg-secondary/10',
      why: 'When trauma is activated, your nervous system needs to feel safe before it can calm down. A safe place image provides an internal resource-a mental space that feels secure, peaceful, and under your control, even when your physical environment feels threatening.',
      how: 'Imagine a place (real or imaginary) where you feel completely safe. Notice every detail: What do you see? Hear? Smell? Feel on your skin? What makes this place safe? Practice visiting this place daily for 2-3 minutes, so it becomes easy to access when you need it during distress.',
      science: 'Safe place imagery activates similar neural networks as being in an actually safe environment. It engages your parasympathetic nervous system (rest and digest), counteracting the sympathetic activation (fight/flight/freeze) of trauma responses.'
    }
  ];

  // If an exercise is active, render it
  if (activeExercise === 'grounding') {
    return (
      <div className="space-y-6 animate-fade-in">
        <Button variant="outline" onClick={() => setActiveExercise(null)} size="lg" className="text-base">
          ← Back to Trauma Tools
        </Button>
        <GroundingExercise onComplete={() => setActiveExercise(null)} />
      </div>
    );
  }

  if (activeExercise === 'safe-place') {
    return (
      <div className="space-y-6 animate-fade-in">
        <Button variant="outline" onClick={() => setActiveExercise(null)} size="lg" className="text-base">
          ← Back to Trauma Tools
        </Button>
        <SafePlaceVisualization />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="bg-gradient-to-br from-background to-secondary/5 border-secondary/20">
        <CardHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-3 rounded-lg bg-secondary/10">
              <Shield className="h-6 w-6 text-secondary" />
            </div>
            <div>
              <CardTitle className="text-2xl">Trauma & PTSD Support</CardTitle>
              <CardDescription>Grounding and stabilization tools for trauma recovery</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="bg-background/50 backdrop-blur-sm rounded-lg p-4 space-y-2">
            <p className="text-sm font-medium flex items-center gap-2">
              <Info className="h-4 w-4 text-secondary" />
              About These Tools
            </p>
            <p className="text-xs text-muted-foreground leading-relaxed">
              These tools focus on <strong>stabilization</strong>-helping you feel safe and present. This is Phase 1 
              of trauma recovery. Processing traumatic memories (Phase 2) should only happen with professional support 
              when you feel ready. Stabilization skills help you manage symptoms and build resources for healing.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Tools Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2 lg:grid-cols-4 bg-gradient-calm border border-border/50">
          {tools.map((tool) => {
            const Icon = tool.icon;
            return (
              <TabsTrigger 
                key={tool.id} 
                value={tool.id}
                className="gap-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
              >
                <Icon className="h-4 w-4" />
                <span className="hidden sm:inline">{tool.name.split(' ')[0]}</span>
              </TabsTrigger>
            );
          })}
        </TabsList>

        {tools.map((tool) => {
          const Icon = tool.icon;
          return (
            <TabsContent key={tool.id} value={tool.id} className="mt-6 space-y-6">
              <Card className="border-border/50">
                <CardHeader>
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${tool.bgColor}`}>
                      <Icon className={`h-5 w-5 ${tool.color}`} />
                    </div>
                    <div className="flex-1">
                      <CardTitle className="text-xl mb-2">{tool.name}</CardTitle>
                      <CardDescription>{tool.description}</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Why This Matters */}
                  <div className="bg-gradient-to-br from-secondary/20 to-primary/15 rounded-lg p-4 space-y-2">
                    <p className="text-base font-medium text-foreground font-aileron">💡 Why This Matters</p>
                    <p className="text-sm text-foreground leading-relaxed">
                      {tool.why}
                    </p>
                  </div>

                  {/* How to Use It */}
                  <div className="bg-gradient-to-br from-primary/20 to-secondary/15 rounded-lg p-4 space-y-2">
                    <p className="text-base font-medium text-foreground font-aileron">🔧 How to Use This Tool</p>
                    <p className="text-sm text-foreground leading-relaxed">
                      {tool.how}
                    </p>
                  </div>

                  {/* The Science */}
                  <div className="bg-gradient-to-br from-secondary/20 to-primary/15 rounded-lg p-4 space-y-2">
                    <p className="text-base font-medium text-foreground font-aileron">🧠 The Science</p>
                    <p className="text-sm text-foreground leading-relaxed">
                      {tool.science}
                    </p>
                  </div>

                  <div className="flex gap-3">
                    <Button 
                      className="flex-1 text-base font-aileron" 
                      onClick={() => {
                        if (tool.id === 'grounding' || tool.id === 'safe-place') {
                          setActiveExercise(tool.id);
                        } else {
                          toast.info(`${tool.name} exercise coming soon!`);
                        }
                      }}
                    >
                      Start Exercise
                    </Button>
                    <Button 
                      variant="outline" 
                      className="flex-1 text-base font-aileron" 
                      onClick={() => toast.info(`${tool.name} guide coming soon!`)}
                    >
                      View Guide
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          );
        })}
      </Tabs>

      {/* Safety Note */}
      <Card className="border-secondary/50 bg-gradient-to-br from-secondary/15 to-primary/10">
        <CardContent className="pt-6">
          <div className="space-y-3">
            <p className="text-base font-medium text-foreground font-aileron">⚠️ Important Safety Information:</p>
            <div className="space-y-2 text-sm text-foreground">
              <p><strong>Don't push through:</strong> If a technique increases distress, stop and try something else. Healing isn't linear.</p>
              <p><strong>Go at your pace:</strong> You're in control. You don't need to "face your trauma" until you're ready and have professional support.</p>
              <p><strong>Build resources first:</strong> Stabilization skills should feel like they help you cope. Practice them when calm, not just in crisis.</p>
              <p><strong>Seek support:</strong> Trauma-focused therapy (EMDR, PE, CPT, TF-CBT) with a qualified professional is the gold standard for recovery.</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};